import React from 'react';
import { Navigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { AdminErrorFallback } from '@/components/ui/ErrorBoundary';

// Admin route imports - COPIED EXACTLY from App.tsx
const BulkEmailCampaign = React.lazy(() => import('@/components/email/BulkEmailCampaign'));
const EmailManagementHub = React.lazy(() => import('@/pages/admin/EmailManagementHub'));

// Admin pages (lazy loaded)
const AdminDashboard = React.lazy(() => import('@/pages/admin/Dashboard'));
import QuotesListPageOriginal from '@/pages/admin/QuotesListPage';
const QuotesListPage = QuotesListPageOriginal;
const PaymentManagement = React.lazy(() => import('@/pages/admin/PaymentManagement'));
const ShippingRoutesPage = React.lazy(() => import('@/pages/admin/ShippingRoutes'));
const StatusManagementPage = React.lazy(() => import('@/pages/admin/StatusManagement'));
const SupportTicketsPage = React.lazy(() => import('@/pages/admin/SupportTickets'));
const UnifiedSupportHub = React.lazy(() => import('@/pages/admin/UnifiedSupportHub'));
const AutoAssignmentPage = React.lazy(() => import('@/pages/admin/AutoAssignment'));
const SystemPerformance = React.lazy(() => import('@/pages/admin/SystemPerformance'));
const EnhancedCustomerManagementPage = React.lazy(() => import('@/components/admin/SimpleCustomerManagement'));
const CustomerProfile = React.lazy(() => import('@/pages/admin/CustomerProfile'));

const AutomationManagementPage = React.lazy(() => import('@/pages/admin/AutomationManagementPage'));

const USWarehouse = React.lazy(() => import('@/pages/admin/USWarehouse'));
const IndiaWarehouse = React.lazy(() => import('@/pages/admin/IndiaWarehouse'));
const ChinaWarehouse = React.lazy(() => import('@/pages/admin/ChinaWarehouse'));
const PackageEntryPage = React.lazy(() => import('@/pages/admin/CleanPackageEntryPage'));
const BulkPackageEntryPage = React.lazy(() => import('@/pages/admin/CleanBulkPackageEntryPage'));
const TrackingManagementPage = React.lazy(() => import('@/pages/admin/TrackingManagementPage'));
const TrackingDetailPage = React.lazy(() => import('@/pages/admin/TrackingDetailPage'));
const ConsolidationWorkflowPage = React.lazy(() => import('@/components/warehouse/ConsolidationWorkflowPage').then((m) => ({ default: m.ConsolidationWorkflowPage })));
const ChatManagement = React.lazy(() => import('@/pages/admin/ChatManagement'));
const LiveChatPage = React.lazy(() => import('@/pages/admin/LiveChatPage'));
const RAGTrainingDashboard = React.lazy(() => import('@/pages/admin/RAGTrainingDashboard'));
const BankAccountSettings = React.lazy(() => import('@/components/admin/BankAccountSettings'));
const SystemSettings = React.lazy(() => import('@/components/admin/SystemSettings'));
const PayUDebugPage = React.lazy(() =>
  import('@/pages/admin/PayUDebugPage').then((m) => ({
    default: m.PayUDebugPage,
  })),
);

// Admin components (lazy loaded with separate chunks for better performance)
const AdminLayout = React.lazy(() => import('@/components/admin/AdminLayout'));
const WAFManagement = React.lazy(() => import('@/pages/admin/WAFManagement'));
const RateLimitManagement = React.lazy(() => import('@/pages/admin/RateLimitManagement'));
const StatusDebug = React.lazy(() => import('@/pages/debug/StatusDebug'));
const DuplicateComponentsPreview = React.lazy(() => import('@/pages/admin/DuplicateComponentsPreview'));
const BlogManagementPage = React.lazy(() => import('@/pages/admin/BlogManagement'));
const MembershipManagementPage = React.lazy(() => import('@/pages/admin/MembershipManagement'));
const EmailDashboard = React.lazy(() => import('@/pages/admin/GmailStyleEmailDashboard'));
const SMSDashboard = React.lazy(() => import('@/pages/admin/SMSDashboard'));
const SMSTemplatesPage = React.lazy(() => import('@/pages/admin/SMSTemplatesPage'));
const SMSCampaignsPage = React.lazy(() => import('@/pages/admin/SMSCampaignsPage'));
const CommunicationsHub = React.lazy(() => import('@/pages/admin/CommunicationsHub'));
const QuoteCalculatorV3 = React.lazy(() => import('@/pages/admin/QuoteCalculatorV3'));
const QuoteReminderSettings = React.lazy(() => import('@/pages/admin/QuoteReminderSettings'));
const ReturnManagement = React.lazy(() => import('@/pages/admin/ReturnManagement'));
const DiscountManagement = React.lazy(() => import('@/components/admin/DiscountManagement'));

// Admin Catalog Management
const AdminCatalogDashboard = React.lazy(() => import('@/pages/admin/AdminCatalogDashboard'));
const AdminProductEditor = React.lazy(() => import('@/pages/admin/AdminProductEditor'));
const AdminOrdersList = React.lazy(() => import('@/pages/admin/AdminOrdersList'));
const PurchaseManagement = React.lazy(() => import('@/pages/admin/PurchaseManagementTable'));

// Smart Intelligence Management pages
const ProductClassificationsManager = React.lazy(() => import('@/pages/admin/ProductClassificationsManager'));
const CountrySettingsManager = React.lazy(() => import('@/pages/admin/CountrySettingsManager'));
const SimpleAdminPricing = React.lazy(() => import('@/pages/admin/SimpleAdminPricing').then((m) => ({ default: m.SimpleAdminPricing })));

// Admin routes (requires admin authentication) - Wrapped with AdminLayout
export const adminRoutes = [
  {
    path: '',
    element: <AdminLayout />,
    children: [
      { index: true, element: <AdminDashboard /> },
      // { path: 'email-templates', element: <EmailTemplatesPage /> }, // Email templates removed
      {
        path: 'quotes',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <QuotesListPage />
          </ErrorBoundary>
        ),
      },
      {
        path: 'orders',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <AdminOrdersList />
          </ErrorBoundary>
        ),
      },
      {
        path: 'purchase-management',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <PurchaseManagement />
          </ErrorBoundary>
        ),
      },
      {
        path: 'quote-calculator',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <QuoteCalculatorV3 />
          </ErrorBoundary>
        ),
      },
      {
        path: 'quote-calculator/:id',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <QuoteCalculatorV3 />
          </ErrorBoundary>
        ),
      },
      {
        path: 'quote-calculator-v2',
        element: <Navigate to="/admin/quote-calculator" replace />,
      },
      {
        path: 'quote-calculator-v2/:id',
        element: <Navigate to="/admin/quote-calculator" replace />,
      },
      {
        path: 'quote-calculator-v3',
        element: <Navigate to="/admin/quote-calculator" replace />,
      },
      {
        path: 'quote-calculator-v3/:id',
        element: <Navigate to="/admin/quote-calculator" replace />,
      },
      // REMOVED: Enhanced Order Management System (legacy)
      // {
      //   path: 'orders',
      //   element: (
      //     <ErrorBoundary fallback={AdminErrorFallback}>
      //       <OrderManagementPage />
      //     </ErrorBoundary>
      //   ),
      // },
      // {
      //   path: 'orders/:id',
      //   element: (
      //     <ErrorBoundary fallback={AdminErrorFallback}>
      //       <OrderDetailPage />
      //     </ErrorBoundary>
      //   ),
      // },
      {
        path: 'automation',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <AutomationManagementPage />
          </ErrorBoundary>
        ),
      },
      {
        path: 'warehouse/us',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <USWarehouse />
          </ErrorBoundary>
        ),
      },
      {
        path: 'warehouse/india',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <IndiaWarehouse />
          </ErrorBoundary>
        ),
      },
      {
        path: 'warehouse/china',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <ChinaWarehouse />
          </ErrorBoundary>
        ),
      },
      {
        path: 'warehouse/:country/consolidation/:requestId/workflow',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <ConsolidationWorkflowPage />
          </ErrorBoundary>
        ),
      },
      {
        path: 'warehouse/packages/new',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <PackageEntryPage />
          </ErrorBoundary>
        ),
      },
      {
        path: 'warehouse/packages/bulk',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <BulkPackageEntryPage />
          </ErrorBoundary>
        ),
      },
      {
        path: 'warehouse/packages/edit/:id',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <PackageEntryPage />
          </ErrorBoundary>
        ),
      },
      {
        path: 'tracking',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <TrackingManagementPage />
          </ErrorBoundary>
        ),
      },
      {
        path: 'tracking/:trackingId',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <TrackingDetailPage />
          </ErrorBoundary>
        ),
      },
      // Catalog Management
      {
        path: 'catalog',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <AdminCatalogDashboard />
          </ErrorBoundary>
        ),
      },
      {
        path: 'catalog/products/new',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <AdminProductEditor />
          </ErrorBoundary>
        ),
      },
      {
        path: 'catalog/products/:handle',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <AdminProductEditor />
          </ErrorBoundary>
        ),
      },
      { path: 'customers', element: <EnhancedCustomerManagementPage /> },
      { path: 'customers/:customerId', element: <CustomerProfile /> },
      // { path: 'users', element: <UserManagementPage /> }, // User management removed
      {
        path: 'support',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <UnifiedSupportHub />
          </ErrorBoundary>
        )
      },
      { path: 'support-tickets', element: <SupportTicketsPage /> },
      { path: 'auto-assignment', element: <AutoAssignmentPage /> },
      {
        path: 'chat-management',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <ChatManagement />
          </ErrorBoundary>
        ),
      },
      {
        path: 'live-chat',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <LiveChatPage />
          </ErrorBoundary>
        ),
      },
      {
        path: 'rag-training',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <RAGTrainingDashboard />
          </ErrorBoundary>
        ),
      },
      {
        path: 'system-performance',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <SystemPerformance />
          </ErrorBoundary>
        ),
      },
      // { path: 'templates', element: <QuoteTemplatesPage /> }, // Component not found - commented out
      {
        path: 'quotes/:id',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <QuoteCalculatorV3 />
          </ErrorBoundary>
        ),
      },
      { path: 'bank-accounts', element: <BankAccountSettings /> },
      { path: 'system-settings', element: <SystemSettings /> },
      {
        path: 'payment-management',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <PaymentManagement />
          </ErrorBoundary>
        ),
      },
      {
        path: 'discounts',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <DiscountManagement />
          </ErrorBoundary>
        ),
      },
      { path: 'shipping-routes', element: <ShippingRoutesPage /> },
      { path: 'status-management', element: <StatusManagementPage /> },
      // { path: 'payment-proofs', element: <PaymentManagementPageNew /> }, // Removed payment proofs page
      { path: 'waf-management', element: <WAFManagement /> },
      { path: 'rate-limit-management', element: <RateLimitManagement /> },
      { path: 'debug/status', element: <StatusDebug /> },
      { path: 'cleanup/duplicates', element: <DuplicateComponentsPreview /> },
      { path: 'debug/payu', element: <PayUDebugPage /> },

      { path: 'blog', element: <BlogManagementPage /> },
      { path: 'memberships', element: <MembershipManagementPage /> },
      { path: 'quote-reminders', element: <QuoteReminderSettings /> },
      { path: 'emails', element: <EmailDashboard /> },
      { path: 'email-campaigns', element: <BulkEmailCampaign /> },
      { path: 'email-management', element: <EmailManagementHub /> },
      { path: 'communications', element: <CommunicationsHub /> },
      { path: 'sms', element: <SMSDashboard /> },
      { path: 'sms-templates', element: <SMSTemplatesPage /> },
      { path: 'sms-campaigns', element: <SMSCampaignsPage /> },
      // Audit logs page removed
      {
        path: 'returns',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <ReturnManagement />
          </ErrorBoundary>
        ),
      },
      // Smart Intelligence Management routes
      {
        path: 'product-classifications',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <ProductClassificationsManager />
          </ErrorBoundary>
        )
      },
      {
        path: 'country-settings',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <CountrySettingsManager />
          </ErrorBoundary>
        )
      },
      {
        path: 'regional-pricing',
        element: (
          <ErrorBoundary fallback={AdminErrorFallback}>
            <SimpleAdminPricing />
          </ErrorBoundary>
        )
      },
    ],
  },
];